import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, InputAdornment, Link, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../../services/api/axios";

const BRAND_BLUE = "#00336C";
const BUSY_STATUSES = ["queued", "running"];

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function publicLink(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export default function WorkspaceAttendees({ workspaceId, onViewExhibitor }) {
  const [attendees, setAttendees] = useState([]);
  const [status, setStatus] = useState({ status: "not_started" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const mounted = useRef(false);
  const loadController = useRef(null);
  const actionController = useRef(null);
  const baseUrl = `/api/trade-show-agent/workspaces/${workspaceId}/attendees`;
  const importing = BUSY_STATUSES.includes(status.status);
  const importStalled = importing && Date.now() - Date.parse(status.updatedAt) > 10 * 60 * 1000;

  const load = useCallback(async () => {
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    try {
      const { data } = await api.get(baseUrl, { signal: controller.signal });
      if (!mounted.current || controller.signal.aborted) return;
      setAttendees(data.attendees);
      setStatus(data.importStatus);
      setError("");
    } catch (requestError) {
      if (!controller.signal.aborted && mounted.current) {
        setError(errorMessage(requestError, "Unable to load attendees."));
      }
    } finally {
      if (!controller.signal.aborted && mounted.current) setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      loadController.current?.abort();
      actionController.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (!importing || saving) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [importing, saving, load]);

  const runAction = async (path, payload) => {
    loadController.current?.abort();
    const controller = new AbortController();
    actionController.current = controller;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`${baseUrl}/${path}`, payload, { signal: controller.signal, timeout: 60000 });
      if (!mounted.current || controller.signal.aborted) return;
      setStatus(data.importStatus);
      if (data.attendees) setAttendees(data.attendees);
      setPage(0);
    } catch (requestError) {
      if (!controller.signal.aborted && mounted.current) setError(errorMessage(requestError, "Unable to import attendees."));
    } finally {
      if (!controller.signal.aborted && mounted.current) setSaving(false);
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 || !file.name.toLowerCase().endsWith(".csv")) {
      setError("Choose a CSV file no larger than 2 MB.");
      return;
    }
    try {
      const csvText = await file.text();
      if (mounted.current) await runAction("upload", { csvText, fileName: file.name.slice(0, 255) });
    } catch {
      if (mounted.current) setError("Unable to read this CSV file.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return attendees.filter((row) => [row.name, row.title, row.companyName, row.email, row.exhibitor?.companyName, row.exhibitor?.boothNumber].some((value) => String(value || "").toLowerCase().includes(query)));
  }, [attendees, search]);
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  const linkedCount = attendees.filter((row) => row.companyId).length;
  const exhibitorCount = attendees.filter((row) => row.exhibitor).length;

  return (
    <Box>
      <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Attendees</Typography>
      <Typography sx={{ color: "#5a6a80", mt: 0.5, mb: 2 }}>
        Attendees from published lists or organizer exports, linked to matching exhibitor companies in this workspace.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={load}>Refresh</Button>}>{error}</Alert>}
      {importing && <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>Looking for a public attendee list and linking attendees to companies…</Alert>}
      {importStalled && <Alert severity="warning" sx={{ mb: 2 }}>This lookup is taking longer than expected. You can retry or upload a CSV.</Alert>}
      {status.status === "unavailable" && <Alert severity="info" sx={{ mb: 2 }}>No supported public attendee list was found. Add a direct attendee list URL or upload an organizer’s CSV below. This does not mean the show has no attendees.</Alert>}
      {status.status === "failed" && <Alert severity="warning" sx={{ mb: 2 }}>The attendee lookup could not finish. Retry or upload an attendee CSV.</Alert>}
      {status.status === "completed" && <Alert severity="success" sx={{ mb: 2 }}>
        Processed {status.imported || 0} attendees; added {status.added || 0} new records.
        {status.skipped > 0 ? ` Skipped ${status.skipped} rows missing a name or company.` : ""}
      </Alert>}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        <TextField label="Public attendee list URL" size="small" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)}
          placeholder={status.sourceUrl || "https://…"} helperText="Supports CSV files and tables with attendee names and companies."
          sx={{ width: { xs: "100%", sm: 480 } }} />
        <Button variant="outlined" startIcon={<RefreshIcon />} disabled={saving || (importing && !importStalled) || loading}
          onClick={() => runAction("discover", { sourceUrl: sourceUrl.trim() })}>Find attendees</Button>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={saving || loading}>
          {saving ? "Importing…" : "Upload attendee CSV"}
          <input hidden type="file" accept=".csv,text/csv" onChange={upload} disabled={saving || loading} />
        </Button>
      </Box>
      <Typography sx={{ color: "#5a6a80", fontSize: "0.85rem", mb: 2 }}>
        CSV columns: Name and Company (required); Title, Company Website, and Email (optional). Maximum 5,000 rows / 2 MB.
        We match exhibitors in this workspace first, then other companies in your organization. New companies are created when no company match exists. Uncertain exhibitor matches remain unlinked.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField size="small" placeholder="Search attendees" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          inputProps={{ "aria-label": "Search attendees" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: { xs: "100%", sm: 480 } }} />
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>{attendees.length} attendees · {linkedCount} linked to companies</Typography>
        <Typography sx={{ color: BRAND_BLUE }}>{exhibitorCount} linked to exhibitors</Typography>
        <Button size="small" onClick={load} disabled={loading || saving}>Refresh links</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Trade show attendees">
          <TableHead><TableRow>{["Attendee", "Title", "Company", "Exhibitor", "Email", "Source"].map((label) => <TableCell key={label} sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6}><CircularProgress size={20} aria-label="Loading attendees" /></TableCell></TableRow> :
              filtered.slice(visiblePage * rowsPerPage, (visiblePage + 1) * rowsPerPage).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell><TableCell>{row.title || "—"}</TableCell>
                  <TableCell>{row.companyId ? <Link component={RouterLink} to={`/prospecting-companies/${row.companyId}`}>{row.companyName}</Link> : <>{row.companyName}<Typography variant="caption" display="block">Not linked</Typography></>}</TableCell>
                  <TableCell>{row.exhibitor ? <>
                    {onViewExhibitor ? <Link component="button" type="button" onClick={() => onViewExhibitor(row.exhibitor.exhibitorId)} sx={{ textAlign: "left" }}>{row.exhibitor.companyName}</Link> : row.exhibitor.companyName}
                    {row.exhibitor.boothNumber && <Typography variant="caption" display="block">Booth {row.exhibitor.boothNumber}</Typography>}
                  </> : "No match"}</TableCell>
                  <TableCell>{row.email || "—"}</TableCell>
                  <TableCell>{publicLink(row.sourceUrl) ? <Link href={publicLink(row.sourceUrl)} target="_blank" rel="noopener noreferrer">View source</Link> : row.sourceLabel || "—"}</TableCell>
                </TableRow>
              ))}
            {!loading && !filtered.length && <TableRow><TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>{search ? "No attendees match your search." : "No attendees imported yet."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filtered.length} page={visiblePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_event, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
    </Box>
  );
}
